import { Form, Input, Button, Checkbox, message, Divider } from 'antd';
import { MailOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { authApi } from '@tradermind/shared/api';
import { useAuthStore } from '@tradermind/shared/stores';
import { registerSchema, type RegisterFormData } from '@tradermind/shared/validators';
import { ROUTES } from '@tradermind/shared/constants';

export default function RegisterPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      nickname: '',
      agreeToTerms: false,
    },
  });

  const registerMutation = useMutation({
    mutationFn: authApi.register,
    onSuccess: (response) => {
      const { user, token, refresh_token } = response.data;
      setAuth(user, token, refresh_token);
      message.success(t('register.success'));
      navigate(ROUTES.DASHBOARD);
    },
    onError: (error: { error?: { message?: string } }) => {
      message.error(error.error?.message || t('register.failed'));
    },
  });

  const onSubmit = (data: RegisterFormData) => {
    registerMutation.mutate({
      email: data.email,
      password: data.password,
      nickname: data.nickname,
    });
  };

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
      <Form.Item
        label={t('register.email')}
        validateStatus={errors.email ? 'error' : ''}
        help={errors.email?.message}
      >
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              prefix={<MailOutlined />}
              placeholder={t('register.email')}
              size="large"
            />
          )}
        />
      </Form.Item>

      <Form.Item
        label={t('register.username')}
        validateStatus={errors.nickname ? 'error' : ''}
        help={errors.nickname?.message}
      >
        <Controller
          name="nickname"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              prefix={<UserOutlined />}
              placeholder={t('register.username')}
              size="large"
            />
          )}
        />
      </Form.Item>

      <Form.Item
        label={t('register.password')}
        validateStatus={errors.password ? 'error' : ''}
        help={errors.password?.message}
      >
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Input.Password
              {...field}
              prefix={<LockOutlined />}
              placeholder={t('register.password')}
              size="large"
            />
          )}
        />
      </Form.Item>

      <Form.Item
        label={t('register.confirm_password')}
        validateStatus={errors.confirmPassword ? 'error' : ''}
        help={errors.confirmPassword?.message}
      >
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field }) => (
            <Input.Password
              {...field}
              prefix={<LockOutlined />}
              placeholder={t('register.confirm_password')}
              size="large"
            />
          )}
        />
      </Form.Item>

      <Form.Item
        validateStatus={errors.agreeToTerms ? 'error' : ''}
        help={errors.agreeToTerms?.message}
      >
        <Controller
          name="agreeToTerms"
          control={control}
          render={({ field }) => (
            <Checkbox checked={field.value} onChange={field.onChange}>
              {t('register.agree_terms')} <Link to={ROUTES.LEGAL.TERMS} target="_blank">{t('register.terms_link')}</Link> {t('register.and')} <Link to={ROUTES.LEGAL.PRIVACY} target="_blank">{t('register.privacy_link')}</Link>
            </Checkbox>
          )}
        />
      </Form.Item>

      <Button
        type="primary"
        htmlType="submit"
        size="large"
        block
        loading={registerMutation.isPending}
      >
        {t('register.submit')}
      </Button>

      <Divider plain>{t('common:or')}</Divider>

      <div style={{ textAlign: 'center' }}>
        {t('register.have_account')} <Link to={ROUTES.AUTH.LOGIN}>{t('register.login_link')}</Link>
      </div>
    </Form>
  );
}
